import type { Dispatch, SetStateAction } from "react"
import { createContext, useContext, useRef, useState } from "react"

import type { StackProps } from "@mui/material"
import { Box, ButtonBase, Fade, Stack, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"
import { get, noop } from "lodash"
import { useInView } from "react-intersection-observer"

import type { FC, FCWC, ResponsiveImageType, SC, TripData, iconKeys } from ".."
import {
  Button,
  ContainerStack,
  IconButton,
  InquiryModal,
  Link,
  Logo,
  NewsletterModal,
  ResponsiveImage,
  SvgIcon,
  TripModal,
} from ".."
import { LinkType } from "../../graphql/types"
import { SiteMetadataContext } from "../../local/wrappers/SiteMetadataProvider"
import { createImageWithVariantAsDefault } from "../../helpers/image-utils"

type FooterNavLink = {
  title: string
  link: LinkType
}

type FooterLogoLink = {
  logo: ResponsiveImageType
  link: LinkType
}

type FooterSliceProps = {
  footerMenuLinksTree: FooterNavLink[]
  footerSecondaryLinksTree: FooterNavLink[]
  footerLogosLinksTree: { logos: FooterLogoLink[] }
}

const socialLinks = [
  {
    uri: "https://www.instagram.com/gowaytravel/",
    title: "instagram",
  },
  {
    uri: "https://www.facebook.com/GowayTravel",
    title: "facebook",
  },
  // {
  //   uri: "https://twitter.com/gowaytravel",
  //   title: "twitter",
  // },
]

const secondaryItems = [
  "BC Reg. #833-0; TICO Reg. #1543989; CST #2070685-20",
  "Offices located in CA, US, AUS",
  `© Copyright ${new Date().getFullYear()}. Goway Travel`,
]

const Footer: SC<{}, FooterSliceProps> = (props) => {
  const [openInquiry, setOpenInquiry] = useState(false)
  const [openTripModal, setOpenTripModal] = useState(false)

  const menuLinks = get(props, "sliceContext.footerMenuLinksTree", [] as FooterNavLink[])
  const logosLinks = get(props, "sliceContext.footerLogosLinksTree.logos", [] as FooterLogoLink[])

  const trip = get(props, "trip", null)
  const showContactInfo = get(props, "showContactInfo", true)

  const siteMetadata = useContext(SiteMetadataContext)

  const callCta = get(props, "sliceContext.callCta", {
    title: `Call ${siteMetadata.phone}`,
    uri: siteMetadata.phoneUrl,
  })

  const [ref, inView] = useInView({
    triggerOnce: true, // Only trigger once
    threshold: 0.5, // Trigger when 50% of the element is in view
  })

  return (
    <>
      <Box component="footer">
        <FooterContainer>
          {showContactInfo && <FooterNewsletter />}
          <FooterContent>
            <FooterLayout mb={{ xs: 20, md: 60.5 }}>
              <Logo size={{ width: { xs: 160, md: 244 }, height: { xs: 42, md: 64 } }} />
              {showContactInfo && (
                <Stack direction="row" spacing={5} sx={{ display: { xs: "none", md: "block" } }}>
                  <Button
                    variant="quaternary"
                    onClick={() => {
                      {
                        if (trip) {
                          setOpenTripModal(true)
                        } else {
                          setOpenInquiry(true)
                        }
                      }
                    }}
                  >
                    Request a Quote
                  </Button>
                  <Button variant="neutral.tertiary" href={callCta.uri}>
                    {callCta.title}
                  </Button>
                </Stack>
              )}
            </FooterLayout>
            <FooterLayout flexWrap="wrap" columnGap={{ md: 25 }} rowGap={{ xs: 5, md: 2 }}>
              <FooterLinks links={menuLinks} />
              <Stack
                direction="row"
                alignSelf={{ xs: "flex-start", md: "flex-end" }}
                alignItems="center"
                columnGap={5}
                mb={{ md: 8 }}
              >
                {socialLinks.map((item, i) => (
                  <IconButton
                    key={i}
                    variant="base"
                    href={item.uri}
                    title={item.title}
                    aria-label={item.title}
                    rel="noopener noreferrer"
                    icon={<SvgIcon variant={item.title as iconKeys} size={{ xs: 14, md: 22 }}/>}
                    sx={(theme) => ({
                      "border": `1px solid ${alpha(theme.palette["neutral.900"].main, 0.08)}`,

                      "&:hover": {
                        bgcolor: (theme) => theme.palette["neutral.100"].main,
                        boxShadow: (theme) =>
                          `1px 1px 2px ${alpha(theme.palette["neutral.900"].main, 0.1)}`,
                      },
                    })}
                  />
                ))}
              </Stack>
            </FooterLayout>
            {showContactInfo && (
              <FooterLayout mt={10} sx={{ display: { xs: "block", md: "none" } }}>
                <Stack direction="column" spacing={3}>
                  <Button variant="quaternary" onClick={() => setOpenInquiry(true)}>
                    Get a quote
                  </Button>
                  <Button variant="neutral.tertiary" href={callCta.uri}>
                    {callCta.title}
                  </Button>
                </Stack>
              </FooterLayout>
            )}
          </FooterContent>
        </FooterContainer>
        <SubFooterContent>
          <Box ref={ref}>
            <Fade in={inView} timeout={1000}>
              <Box
                display={{ xs: "grid", lg: "flex" }}
                gridTemplateColumns={{
                  xs: "repeat(3, minmax(0, 1fr))",
                  lg: "none",
                }}
                justifyContent={{ lg: "space-between" }}
                columnGap={4}
              >
                {logosLinks.map((logo, i) => (
                  <Stack key={i} alignItems="center" justifyContent="center" height={100}>
                    <ResponsiveImage
                      image={createImageWithVariantAsDefault(logo.logo, "original")}
                      maxHeight={{ xs: 100, lg: 40 }}
                      maxWidth={{ xs: 80, lg: 100 }}
                      sx={{
                        "transition": (theme) =>
                          theme.transitions.create("filter", {
                            duration: theme.transitions.duration.standard,
                          }),
                        "&:hover": {
                          filter:
                            "brightness(0) saturate(100%) invert(25%) sepia(7%) saturate(182%) hue-rotate(251deg) brightness(95%) contrast(88%)",
                        },
                      }}
                    />
                  </Stack>
                ))}
              </Box>
            </Fade>
          </Box>
          <Stack direction={{ md: "row" }} columnGap={10} rowGap={7} flexWrap="wrap">
            {secondaryItems.map((item, i) => (
              <Typography
                key={i}
                variant="body.xs"
                color={(theme) => alpha(theme.palette["neutral.700"].main, 0.8)}
              >
                {item}
              </Typography>
            ))}
          </Stack>
        </SubFooterContent>
      </Box>
      <InquiryModal open={openInquiry} onClose={() => setOpenInquiry(false)} />
      {trip && (
        <TripModal
          open={openTripModal}
          onClose={() => setOpenTripModal(false)}
          trip={trip as TripData}
        />
      )}
    </>
  )
}

export default Footer

/** A sub-component for the newsletter sign up button in the footer */
const FooterNewsletter: FC = () => {
  const [openNewsletter, setOpenNewsletter] = useState(false)

  return (
    <>
      <ButtonBase
        disableRipple
        onClick={() => setOpenNewsletter(true)}
        sx={{
          "bgcolor": (theme) => theme.palette["tertiary.300"].main,
          "borderBottom": (theme) => `1px solid ${alpha(theme.palette["neutral.900"].main, 0.1)}`,
          "display": "block",
          "py": 3.5,
          "transition": (theme) =>
            theme.transitions.create(["background-color"], {
              duration: theme.transitions.duration.standard,
            }),
          "width": "100%",

          "&:hover": {
            bgcolor: (theme) => theme.palette["tertiary.800"].main,
          },
        }}
      >
        <ContainerStack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="link.lg">Sign Up For Our Newsletter</Typography>
          <Stack width={62} height={62} alignItems="center" justifyContent="center">
            <SvgIcon variant="arrowOutward" size={{ xs: 16, md: 24 }} />
          </Stack>
        </ContainerStack>
      </ButtonBase>
      <NewsletterModal open={openNewsletter} onClose={() => setOpenNewsletter(false)} />
    </>
  )
}

/** A sub-component for the container of the footer */
const FooterContainer: FCWC = ({ children }) => (
  <Box bgcolor={(theme) => theme.palette["tertiary.300"].main}>{children}</Box>
)

/** A sub-component for the content of the footer */
const FooterContent: FCWC = ({ children }) => (
  <Box py={{ xs: 10, md: 19 }} pb={{ md: 11 }}>
    {children}
  </Box>
)

/** A sub-component for the layout in the content of the footer */
const FooterLayout: FC<StackProps> = (props) => (
  <ContainerStack
    direction={{ xs: "column", md: "row" }}
    alignItems={{ md: "center" }}
    justifyContent={{ md: "space-between" }}
    {...props}
  />
)

/** A sub-component for the sub-footer content in the footer */
const SubFooterContent: FCWC = ({ children }) => (
  <ContainerStack pt={{ lg: 5 }} pb={{ xs: 5, lg: 10 }} rowGap={{ xs: 10, lg: 7.5 }}>
    {children}
  </ContainerStack>
)

let linkId = 0
// A context to store the unique id of this link for hover state
const FooterLinkItemHoverContext = createContext<
  [hoverId: string, setHoverId: Dispatch<SetStateAction<string>>]
>(["", noop])

const FooterLinks: FC<{ links: FooterNavLink[] }> = ({ links }) => {
  return (
    <FooterLinkItemHoverContext.Provider value={useState<string>("")}>
      <Box sx={{ columnCount: { xs: 2, md: 4 }, columnGap: { xs: 32.5, md: 25 } }}>
        {links.map((item, i) => (
          <Box key={i} mb={{ xs: 5, md: 8 }}>
            <FooterLinkItem uri={item.link.uri} title={item.title} />
          </Box>
        ))}
      </Box>
    </FooterLinkItemHoverContext.Provider>
  )
}

const FooterLinkItem: FC<LinkType> = ({ uri, title }) => {
  const [hoverId, setHoverId] = useContext(FooterLinkItemHoverContext)
  const { current: uniqueId } = useRef(`${linkId++}`)

  return (
    <Link
      variant="link.sm"
      type="base"
      href={uri}
      onMouseEnter={() => setHoverId(uniqueId)}
      onMouseLeave={() => setHoverId("")}
      onFocus={() => setHoverId(uniqueId)}
      onBlur={() => setHoverId("")}
      underline="none"
      sx={{
        "transition": (theme) => theme.transitions.create(["color", "opacity"]),
        ...(hoverId !== "" && { opacity: 0.7 }),

        "&:hover, &:focus-visible": {
          opacity: 1,
          outline: "none",
        },
      }}
    >
      {title}
    </Link>
  )
}
